// atomConstants.ts

import { setInitialPlacementStatusMetrics } from '@client/core/atoms/metrics.js';
import { $prebidTargetingAtom } from '@client/core/atoms/prebidTargeting.js';
import { $wallpaperConfig } from '@client/core/atoms/wallpaperConfigAtom.js';
import { events, messageBus } from '@schibsted-nmp/advertising-events';
import {
  AdServer,
  type AfsPlacement,
  type ClientAdPlacement,
  ConsentStatus,
  debugLog,
  devToolToggled,
  type InitialState,
  type RetailMediaPlacement,
  setAdvtState
} from '@schibsted-nmp/advertising-shared';

import { $afsAtom } from './afs.js';
import { $amplitudeExperimentsAtom } from './amplitude.js';
import {
  $config,
  $initialState,
  $ppidAtom,
  getEmptyOrPersistedPPID
} from './config.js';
import { $consentAtom } from './consent.js';
import { $gamTargetingAtom } from './gamTargeting.js';
import {
  $placementsMap,
  $statusSequence,
  addStatusToSequence,
  getPlacementList,
  listenPlacementKeysForValueChange
} from './placements.js';
import { $retailMediaAtom } from './retailMedia.js';
import { $unleashFeaturesAtom } from './unleashFeatures.js';
import {
  hasQueryOrCategories,
  isRetailMediaOverride
} from '../utils/retailMediaUtils.js';

export function setInitialGlobalAtoms(
  initialState: InitialState,
  consent: ConsentStatus
) {
  $config.set(initialState.config);
  $ppidAtom.set(getEmptyOrPersistedPPID());
  $gamTargetingAtom.set(initialState.config.adServer.gam?.targeting ?? []);
  $prebidTargetingAtom.set(
    initialState.config.adServer.prebid?.targeting ?? []
  );
  $consentAtom.set(consent);
  $unleashFeaturesAtom.set(initialState.unleashFeatures);

  const allowedAdservers = [AdServer.Afs, AdServer.Gam, AdServer.Retailmedia];

  const allowedPlacements = initialState.config.placements.filter((placement) =>
    allowedAdservers.includes(placement.adServer.type as AdServer)
  );

  const placementsMap: Record<string, ClientAdPlacement> =
    allowedPlacements.reduce<Record<string, ClientAdPlacement>>(
      (map, placement) => {
        map[placement.placementId] = {
          ...placement,
          status: 'pending',
          creativeType: undefined,
          statusSequence: []
        } as const satisfies ClientAdPlacement;

        return map;
      },
      {}
    );

  if (devToolToggled() || initialState.env === 'local') {
    for (const placement of allowedPlacements) {
      listenPlacementKeysForValueChange(
        placement.placementId,
        'status',
        (status, oldStatus) => {
          if (status !== oldStatus) {
            addStatusToSequence(placement.placementId, status);
          }
        }
      );
    }
  }

  $placementsMap.set(placementsMap);

  // Log placements with sponsored content override
  const sponsoredPlacements = allowedPlacements.filter(
    (p) =>
      'overrideAdServer' in p &&
      p.overrideAdServer === AdServer.SponsoredContent
  );

  if (sponsoredPlacements.length > 0) {
    debugLog(
      '[Client] Placements with sponsored content override:',
      sponsoredPlacements.map((p) => ({
        placementId: p.placementId,
        overrideAdServer: 'overrideAdServer' in p ? p.overrideAdServer : null
      }))
    );
  }

  $afsAtom.set({
    config: initialState.config.adServer.afs ?? null,
    placements: initialState.config.placements.filter(
      (placement): placement is AfsPlacement =>
        placement.adServer.type === AdServer.Afs
    )
  });

  const retailMediaPlacements = initialState.config.placements.filter(
    (placement): placement is RetailMediaPlacement =>
      placement.adServer.type === AdServer.Retailmedia
  );

  const hasRetailMediaOverride = initialState.config.placements.some(
    isRetailMediaOverride
  );

  if (
    (retailMediaPlacements.length > 0 || hasRetailMediaOverride) &&
    hasQueryOrCategories(initialState.searchParams)
  ) {
    $retailMediaAtom.set({
      placements: retailMediaPlacements,
      searchParams: initialState.searchParams,
      eventType: 'init'
    });
  }

  setInitialPlacementStatusMetrics(getPlacementList());

  $amplitudeExperimentsAtom.set(initialState.amplitudeExperiments);

  $initialState.set({
    env: initialState.env,
    baseUrl: initialState.baseUrl,
    logSessionId: initialState.logSessionId,
    locale: initialState.locale,
    unleashFeatures: initialState.unleashFeatures,
    amplitudeExperiments: initialState.amplitudeExperiments,
    config: initialState.config
  });
}

function makeState(): ADVT.State {
  function state() {
    return state as ADVT.State;
  }

  Object.defineProperties(state, {
    initialState: {
      get() {
        return $initialState.get();
      },
      enumerable: true
    },

    config: {
      get() {
        return $config.get();
      },
      enumerable: true
    },

    ppid: {
      get() {
        return $ppidAtom.get();
      },
      enumerable: true
    },

    placements: {
      get() {
        return $placementsMap.get();
      },
      enumerable: true
    },

    afs: {
      get() {
        return $afsAtom.get();
      },
      enumerable: true
    },

    unleashFeatures: {
      get() {
        return $unleashFeaturesAtom.get();
      },
      enumerable: true
    },

    gamTargeting: {
      get() {
        return $gamTargetingAtom.get();
      },
      enumerable: true
    },

    prebidTargeting: {
      get() {
        return $prebidTargetingAtom.get();
      },
      enumerable: true
    },

    statusSequence: {
      get() {
        return $statusSequence.get();
      },
      enumerable: true
    }
  } as const satisfies {
    [TKey in keyof ADVT.State]: PropertyDescriptor & {
      get(): ADVT.State[TKey];
    };
  });

  return state as ADVT.State;
}

setAdvtState('state', makeState());

// setWallpaperConfig will be removed once the creative is updated to post message to eventListener
function setWallpaperConfig(...args: Parameters<typeof $wallpaperConfig.set>) {
  $wallpaperConfig.set(...args);
}

setAdvtState('setWallpaperConfig', setWallpaperConfig);

// Helper to check sponsored content configuration
function checkSponsoredContent() {
  const config = $config.get();
  const placements = getPlacementList();

  const sponsoredPlacements = placements.filter(
    (p) =>
      'overrideAdServer' in p &&
      p.overrideAdServer === AdServer.SponsoredContent
  );

  console.group('Sponsored Content Configuration');
  console.log('Total placements:', placements.length);
  console.log('Sponsored content placements:', sponsoredPlacements.length);

  if (sponsoredPlacements.length > 0) {
    console.table(
      sponsoredPlacements.map((p) => ({
        placementId: p.placementId,
        overrideAdServer: 'overrideAdServer' in p ? p.overrideAdServer : null,
        status: p.status,
        creativeType: p.creativeType
      }))
    );
  } else {
    console.log('No placements configured for sponsored content');
  }

  if (config) {
    console.log('\nRaw backend config placements:');
    console.log(
      config.placements
        .filter(
          (p) =>
            p.adServer.type === AdServer.Gam &&
            p.adServer.config.overrideAdServer === AdServer.SponsoredContent
        )
        .map((p) => ({
          placementId: p.placementId,
          overrideAdServer:
            p.adServer.type === AdServer.Gam
              ? p.adServer.config.overrideAdServer
              : null
        }))
    );
  }

  console.groupEnd();

  return sponsoredPlacements;
}

setAdvtState('checkSponsoredContent', checkSponsoredContent);

// Helper to simulate sponsored content for local testing
function simulateSponsoredContent(
  hasContent = true,
  placementId = 'advt_middle_1'
) {
  console.group('Simulating Sponsored Content');
  console.log('Placement:', placementId);
  console.log(
    'Broadcasting:',
    hasContent ? 'has content' : 'no content (will fallback to GAM)'
  );

  // Use placement-specific event topic to prevent cross-contamination
  const eventTopic = `render-sponsored-content:${placementId}`;

  messageBus.publish(events.PODLET.channel, eventTopic, hasContent);

  console.log('MessageBus event sent to:', eventTopic);
  console.log('Check placement status with: ADVT.checkSponsoredContent()');
  console.groupEnd();

  return hasContent;
}

setAdvtState('simulateSponsoredContent', simulateSponsoredContent);

// Helper to check what backend sent for all placements
function inspectBackendConfig(): ReturnType<typeof $config.get> | undefined {
  const config = $config.get();

  if (!config) {
    console.error('No config loaded yet');
    return undefined;
  }

  console.group('Backend Configuration Inspector');
  console.log('Brand:', config.brand);
  console.log('Page Type:', config.pageType);
  console.log('Vertical:', config.vertical);
  console.log('Device:', config.deviceType);

  console.log('\nAll Placements from Backend:');
  console.table(
    config.placements.map((p) => ({
      placementId: p.placementId,
      adServerType: p.adServer.type,
      overrideAdServer:
        p.adServer.type === AdServer.Gam
          ? (p.adServer.config.overrideAdServer ?? null)
          : 'N/A',
      hasOverride: 'overrideAdServer' in p ? p.overrideAdServer : null
    }))
  );

  const sponsoredFromBackend = config.placements.filter(
    (p) =>
      p.adServer.type === AdServer.Gam &&
      p.adServer.config.overrideAdServer === AdServer.SponsoredContent
  );

  if (sponsoredFromBackend.length === 0) {
    console.log(
      '\nBackend did NOT send overrideAdServer: "sponsored-content" for any placements'
    );
    console.log(
      'This is expected for recommerce - sponsored content is only for real estate & mobility'
    );
  } else {
    console.log(
      '\nBackend sent overrideAdServer for:',
      sponsoredFromBackend.map((p) => p.placementId)
    );
  }

  console.groupEnd();

  return config;
}

setAdvtState('inspectBackendConfig', inspectBackendConfig);
